export const REVENUE_COPILOT = {
  id: "revenue",
  name: "Sales & Projects",
  tagline: "Lives inside your CRM and projects",
  heroQuestion: "Ask about deals, projects, or sold versus delivered.",
  roles: [
    {
      id: "owner",
      label: "Owner",
      question: "What needs my attention today?",
      tools: ["Reading open deals", "Scanning recent activities", "Checking project milestones", "Reviewing overdue items"],
      answer: {
        summary: "Three things deserve your attention this morning. Everything else is on track.",
        sections: [
          {
            title: "Needs attention",
            items: [
              { tone: "amber", label: "Larsen Contracting — $48K deal idle 12 days", detail: "Last touch was the quote on the 3rd. A two-line check-in from Dana today would likely close it." },
              { tone: "amber", label: "Cedar Ridge — framing milestone 5 days behind", detail: "Materials arrived late Thursday. A client update is drafted and ready to send." },
              { tone: "rose", label: "Invoice #1042 — $18,400, 21 days overdue", detail: "Harborview Properties. A polite reminder is queued for your approval." },
            ],
          },
          {
            title: "Quietly on track",
            items: [
              { tone: "teal", label: "6 active deals progressing normally", detail: "No intervention needed this week." },
              { tone: "teal", label: "Harborview phase 2 ahead of schedule", detail: "Delivered work is running 8% ahead of plan." },
            ],
          },
        ],
        actions: ["Follow-up task created for Dana — Larsen, today 2:00 pm", "Cedar Ridge client update drafted — review & send"],
        sticky: "One question in, a whole morning back. The answer arrived as a short list, not a dashboard to decode.",
      },
    },
    {
      id: "sales",
      label: "Sales / Account Manager",
      question: "Which deals are stuck and what should I do next?",
      tools: ["Reading all open deals", "Scanning activity history", "Scoring deal staleness"],
      answer: {
        summary: "Three deals have gone quiet. Here's each one — and the single best next move.",
        sections: [
          {
            title: "Stuck deals",
            items: [
              { tone: "rose", label: "Larsen Contracting — $48K · idle 12 days", detail: "Quote stage. Call, don't email — they asked two detailed questions last time, which signals real intent." },
              { tone: "amber", label: "Brookfield Dental — $22K · idle 9 days", detail: "Waiting on your revised scope. Sending it today unblocks the signature." },
              { tone: "teal", label: "Summit Legal — $65K · 6 days", detail: "Verbal yes, contract with their counsel. A gentle nudge Friday keeps it warm without pressure." },
            ],
          },
        ],
        actions: ["3 follow-up tasks created and assigned with talking points — each one waits for your yes before it goes out"],
        sticky: "No pipeline review meeting. Three deals, three next moves, tasks already created.",
      },
    },
    {
      id: "pm",
      label: "Project Manager",
      question: "What's the real status of sold versus delivered?",
      tools: ["Reading won deals", "Reading active projects", "Reading tasks & timesheets"],
      answer: {
        summary: "Four engagements sold, three delivering well, one hasn't started — and it's the one nobody noticed.",
        sections: [
          {
            title: "Sold vs delivered",
            items: [
              { tone: "teal", label: "Cedar Ridge — sold $120K · 68% delivered", detail: "On track despite the framing delay. Client communication is the only gap." },
              { tone: "teal", label: "Harborview — sold $86K · 41% delivered", detail: "Running 8% ahead of schedule. Margin healthy at 31%." },
              { tone: "teal", label: "Larsen — sold $48K · 92% delivered", detail: "Nearly done. Final walkthrough not yet booked." },
              { tone: "rose", label: "Brookfield — sold $22K · 15% delivered", detail: "Kickoff never scheduled. This is the one that will surprise you in three weeks if it slips further." },
            ],
          },
        ],
        actions: ["Kickoff task created for Brookfield — Marcus, this week", "Final walkthrough reminder set for Larsen"],
        sticky: "Sold vs delivered on one screen — and the quiet risk surfaced before it became a client call.",
      },
    },
  ],
  suggestions: ["What did we close last quarter?", "Create a follow-up for Larsen", "Which project is most at risk?"],
};

export const BOOKS_COPILOT = {
  id: "books",
  name: "Books",
  tagline: "Lives inside your books",
  heroQuestion: "Ask your financials a plain question.",
  roles: [
    {
      id: "accounting",
      label: "Accounting · Billing",
      question: "What can I invoice right now?",
      tools: ["Reading delivered milestones", "Checking unbilled work", "Matching to contracts"],
      answer: {
        summary: "$34,200 is billable today across three projects — none of it invoiced yet.",
        sections: [
          {
            title: "Ready to bill",
            items: [
              { tone: "teal", label: "Harborview — $16,800", detail: "Phase 2 sign-off received Monday. Fully billable under the contract terms." },
              { tone: "teal", label: "Cedar Ridge — $12,400", detail: "Framing milestone delivered. Invoice can reference the milestone certificate." },
              { tone: "teal", label: "Larsen — $5,000", detail: "Change order #3 approved and complete." },
            ],
          },
        ],
        actions: ["3 draft invoices prepared — waiting for your review"],
        sticky: "Unbilled work found in seconds. That's cash that was already earned, just sitting unasked-for.",
      },
    },
    {
      id: "accounting-outstanding",
      label: "Accounting · Collections",
      question: "What's outstanding — and who should we nudge?",
      tools: ["Reading open invoices", "Checking payment history", "Ranking by days overdue"],
      answer: {
        summary: "$52,800 is outstanding. One invoice needs a real nudge; the other two just need routine reminders.",
        sections: [
          {
            title: "Outstanding",
            items: [
              { tone: "rose", label: "#1042 · Harborview — $18,400 · 21 days overdue", detail: "They pay slowly but always pay. A phone call works better than a third email." },
              { tone: "amber", label: "#1038 · Summit Legal — $21,200 · 9 days", detail: "Within their normal 10-day pattern. Automated reminder is enough." },
              { tone: "teal", label: "#1044 · Larsen — $13,200 · 2 days", detail: "Fresh. No action needed yet." },
            ],
          },
        ],
        actions: ["Reminder scheduled for #1038", "Call task created for #1042 with talking points"],
        sticky: "Not a report to read — a decision already made about who needs what kind of nudge.",
      },
    },
    {
      id: "owner-cash",
      label: "Owner · Cash",
      question: "How's our cash position?",
      tools: ["Reading cash balances", "Reading receivables", "Projecting next 30 days"],
      answer: {
        summary: "Healthy. You have $186,400 on hand, $52,800 on the way, and $71,000 expected in the next 30 days.",
        sections: [
          {
            title: "Position today",
            items: [
              { tone: "teal", label: "Cash on hand — $186,400", detail: "Comfortably above your 2-month operating cushion." },
              { tone: "teal", label: "Receivables — $52,800", detail: "Mostly collectable; one slow payer (Harborview) being handled." },
              { tone: "teal", label: "Next 30 days — $71,000 expected", detail: "Includes the $34,200 you can invoice today." },
            ],
          },
        ],
        actions: ["Weekly cash summary now lands in your inbox every Monday 7am"],
        sticky: "The 9pm-with-a-laptop question, answered at 9am in one breath.",
      },
    },
    {
      id: "owner-margin",
      label: "Owner · Profit",
      question: "Which projects are actually profitable?",
      tools: ["Reading project costs", "Reading timesheets", "Comparing margins"],
      answer: {
        summary: "Two projects are healthy, one is quietly leaking — and you can still fix it.",
        sections: [
          {
            title: "Profitability",
            items: [
              { tone: "teal", label: "Harborview — 31% margin", detail: "Your best-run project this year. Worth studying what went right." },
              { tone: "teal", label: "Cedar Ridge — 24% margin", detail: "Solid, despite the materials delay." },
              { tone: "amber", label: "Larsen — 12% margin", detail: "Materials overrun ate 9 points. A change-order review before close-out could recover half of it." },
            ],
          },
        ],
        actions: ["Change-order review task created for Larsen close-out"],
        sticky: "Margin by project, with the fix attached — before the project closes and the leak becomes permanent.",
      },
    },
  ],
  suggestions: ["What can I bill this week?", "Any invoice older than 30 days?", "What's our profit on Cedar Ridge?"],
};
